import { useState } from 'react'
import { Download, Plus } from 'lucide-react'
import { toast } from 'sonner'
import { Button, Chip, Drawer, Input, Select, Textarea } from '@/components/ui'
import {
  rmaPdfUrl,
  useAddRmaLine,
  useDeleteRmaLine,
  usePatchRmaHeader,
  usePatchRmaLine,
  useRmaDetail,
  useTransitionRma,
} from '../api/queries'
import type { RmaLine, Stage } from '../api/types'
import {
  ALL_STAGES,
  REASON_LABELS,
  STAGE_LABELS,
  STAGE_VARIANT,
  TERMINAL_STAGES,
  transitionAllowed,
} from '../utils/stage-meta'
import { RmaLineGrid } from './RmaLineGrid'
import { StageHistoryTimeline } from './StageHistoryTimeline'

export interface RmaDetailDrawerProps {
  rmaId: string | null
  onOpenChange: (open: boolean) => void
}

export function RmaDetailDrawer({ rmaId, onOpenChange }: RmaDetailDrawerProps) {
  const { data: rma, isLoading } = useRmaDetail(rmaId)
  const patchHeader = usePatchRmaHeader()
  const patchLine = usePatchRmaLine()
  const deleteLine = useDeleteRmaLine()
  const addLine = useAddRmaLine()
  const transition = useTransitionRma()

  const [notesDraft, setNotesDraft] = useState<string | null>(null)
  const [ownerNameDraft, setOwnerNameDraft] = useState<string | null>(null)
  const [ownerPhoneDraft, setOwnerPhoneDraft] = useState<string | null>(null)

  if (!rmaId) return null

  const editable = rma ? !TERMINAL_STAGES.has(rma.status) : false
  const availableTargets = rma
    ? ALL_STAGES.filter((s) => transitionAllowed(rma.status, s))
    : []

  function commitNotes() {
    if (!rma || notesDraft === null || notesDraft === (rma.notes ?? '')) {
      setNotesDraft(null)
      return
    }
    patchHeader.mutate(
      { rmaId: rma.id, edits: { notes: notesDraft } },
      {
        onError: (err) =>
          toast.error(`Update failed${err instanceof Error ? `: ${err.message}` : ''}`),
      },
    )
    setNotesDraft(null)
  }

  function commitOwnerName() {
    if (!rma || ownerNameDraft === null || ownerNameDraft === (rma.owner_name ?? '')) {
      setOwnerNameDraft(null)
      return
    }
    patchHeader.mutate(
      { rmaId: rma.id, edits: { owner_name: ownerNameDraft } },
      {
        onError: (err) =>
          toast.error(`Update failed${err instanceof Error ? `: ${err.message}` : ''}`),
      },
    )
    setOwnerNameDraft(null)
  }

  function commitOwnerPhone() {
    if (!rma || ownerPhoneDraft === null || ownerPhoneDraft === (rma.owner_phone ?? '')) {
      setOwnerPhoneDraft(null)
      return
    }
    patchHeader.mutate(
      { rmaId: rma.id, edits: { owner_phone: ownerPhoneDraft } },
      {
        onError: (err) =>
          toast.error(`Update failed${err instanceof Error ? `: ${err.message}` : ''}`),
      },
    )
    setOwnerPhoneDraft(null)
  }

  return (
    <Drawer
      open={!!rmaId}
      onOpenChange={onOpenChange}
      size="wide"
      ident={rma?.rma_number ?? 'Pending #'}
      title={rma?.customer_name ?? 'Return authorization'}
      meta={
        rma ? (
          <div className="flex items-center gap-2">
            <Chip size="xs" variant={STAGE_VARIANT[rma.status]} dot>
              {STAGE_LABELS[rma.status]}
            </Chip>
            <span className="text-role-metadata">{REASON_LABELS[rma.reason_code]}</span>
          </div>
        ) : null
      }
      headerActions={
        rma?.has_pdf ? (
          <Button
            variant="secondary"
            size="sm"
            onClick={() => window.open(rmaPdfUrl(rma.id), '_blank')}
          >
            <Download size={14} className="mr-1.5" />
            Download PDF
          </Button>
        ) : null
      }
    >
      {isLoading || !rma ? (
        <div className="px-6 py-4 text-sm text-tertiary">Loading…</div>
      ) : (
        <div className="flex flex-col gap-6 px-6 py-4">
          <section className="flex flex-col gap-2">
            <div className="flex items-center gap-3">
              <label className="flex flex-1 flex-col gap-1 text-sm">
                <span className="text-secondary">Move stage</span>
                <Select
                  value=""
                  disabled={availableTargets.length === 0 || transition.isPending}
                  onChange={(e) => {
                    const target = e.target.value as Stage
                    if (!target) return
                    transition.mutate(
                      { rmaId: rma.id, targetStage: target },
                      {
                        onSuccess: () => toast.success(`Moved to ${STAGE_LABELS[target]}`),
                        onError: (err) =>
                          toast.error(
                            `Transition failed${err instanceof Error ? `: ${err.message}` : ''}`,
                          ),
                      },
                    )
                  }}
                >
                  <option value="">Select a stage…</option>
                  {availableTargets.map((s) => (
                    <option key={s} value={s}>
                      {STAGE_LABELS[s]}
                    </option>
                  ))}
                </Select>
              </label>
            </div>
            {rma.original_so_name ? (
              <div className="text-role-metadata">Original SO: {rma.original_so_name}</div>
            ) : null}
            {rma.requested_by ? (
              <div className="text-role-metadata">Requested by {rma.requested_by}</div>
            ) : null}
            {rma.approved_by ? (
              <div className="text-role-metadata">
                Approved by {rma.approved_by}
                {rma.approved_at ? ` on ${new Date(rma.approved_at).toLocaleDateString()}` : null}
              </div>
            ) : null}
            <div className="flex items-center gap-3">
              <label className="flex flex-1 flex-col gap-1 text-sm">
                <span className="text-secondary">RMA owner</span>
                <Input
                  value={ownerNameDraft ?? rma.owner_name ?? ''}
                  onChange={(e) => setOwnerNameDraft(e.target.value)}
                  onBlur={commitOwnerName}
                  disabled={!editable}
                  placeholder="Who's handling this return"
                />
              </label>
              <label className="flex flex-1 flex-col gap-1 text-sm">
                <span className="text-secondary">Phone number</span>
                <Input
                  value={ownerPhoneDraft ?? rma.owner_phone ?? ''}
                  onChange={(e) => setOwnerPhoneDraft(e.target.value)}
                  onBlur={commitOwnerPhone}
                  disabled={!editable}
                  placeholder="(817) 555-0100"
                />
              </label>
            </div>
          </section>

          <section className="flex flex-col gap-2">
            <div className="flex items-center justify-between">
              <h3 className="m-0 text-sm font-semibold text-primary">Lines</h3>
              <Button
                variant="ghost"
                size="sm"
                disabled={!editable}
                onClick={() =>
                  addLine.mutate(
                    { rmaId: rma.id, line: {} },
                    {
                      onError: (err) =>
                        toast.error(
                          `Add line failed${err instanceof Error ? `: ${err.message}` : ''}`,
                        ),
                    },
                  )
                }
              >
                <Plus size={12} /> Add line
              </Button>
            </div>
            <RmaLineGrid
              lines={rma.lines}
              disabled={!editable}
              onLineEdit={(lineId, edits) =>
                patchLine.mutate(
                  { rmaId: rma.id, lineId, edits },
                  {
                    onError: (err) =>
                      toast.error(
                        `Line update failed${err instanceof Error ? `: ${err.message}` : ''}`,
                      ),
                  },
                )
              }
              onLineRemove={(lineId: RmaLine['id']) =>
                deleteLine.mutate(
                  { rmaId: rma.id, lineId },
                  {
                    onError: (err) =>
                      toast.error(
                        `Remove failed${err instanceof Error ? `: ${err.message}` : ''}`,
                      ),
                  },
                )
              }
            />
          </section>

          <section className="flex flex-col gap-2">
            <h3 className="m-0 text-sm font-semibold text-primary">Notes</h3>
            <Textarea
              value={notesDraft ?? rma.notes ?? ''}
              onChange={(e) => setNotesDraft(e.target.value)}
              onBlur={commitNotes}
              disabled={!editable}
              rows={3}
              placeholder="Internal notes about this return…"
            />
          </section>

          <section className="flex flex-col gap-2">
            <h3 className="m-0 text-sm font-semibold text-primary">History</h3>
            <StageHistoryTimeline entries={rma.stage_history} />
          </section>
        </div>
      )}
    </Drawer>
  )
}
